import { Component, OnInit, OnDestroy, inject, signal, computed, effect, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { ToastMessageService } from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import {
  EntityStatusComponent,
  WithLinkComponent,
} from '@fitness-force/shared/data-grid-exports';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { TENANTS_INFO } from '@fitness-force/state';
import { MilestoneConfigService } from '../../services/milestone-config.service';
import {
  ListMilestoneConfigResponse,
  MilestoneConfigForDisplay,
  MilestoneType,
  MilestonePeriodType,
  MilestoneRewardType
} from '../../models/milestone.models';

@Component({
  selector: 'lib-milestone-config-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DataGridComponent,
    ButtonModule,
    TranslateModule,
    BreadcrumbComponent,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    DrawerModule,
    AuditTrailMiniComponent,
  ],
  templateUrl: './milestone-config-list.component.html',
  styleUrls: ['./milestone-config-list.component.scss']
})
export class MilestoneConfigListComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly milestoneService = inject(MilestoneConfigService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  private readonly tenantInfo = toSignal(this.store.select(TENANTS_INFO));
  protected readonly tenantId = computed(
    () => this.tenantInfo()?.selected_tenant?.id ?? 0,
  );

  protected readonly searchText = signal('');
  protected selectedStatusFilter = '';
  protected readonly allMilestones = signal<MilestoneConfigForDisplay[]>([]);

  protected readonly statusFilters = [
    { name: 'ADMIN.COMMON.ALL', value: '' },
    { name: 'ADMIN.COMMON.ACTIVE', value: 'active' },
    { name: 'ADMIN.COMMON.INACTIVE', value: 'inactive' },
  ];

  protected readonly showAuditTrailSidebar = signal(false);
  protected readonly showAuditLogsFeature = signal(false);

  protected readonly tableData = signal<MilestoneConfigForDisplay[]>([]);
  protected readonly columns = signal<any[]>([]);
  protected readonly loading = signal<boolean>(false);
  protected readonly emptyStateComponent = signal<any>(NoComponentComponent);
  protected readonly selectAllData = signal(false);
  protected readonly totalRecordLength = signal({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
  });

  protected readonly tableData$ = new BehaviorSubject<MilestoneConfigForDisplay[]>([]);
  protected readonly columns$ = new BehaviorSubject<any[]>([]);
  protected readonly totalRecordLength$ = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
  });
  protected readonly selectAllData$ = new BehaviorSubject(false);
  protected readonly emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);

  currentPage = signal(1);
  pageSize = signal(10);

  constructor() {
    effect(() => {
      this.tableData$.next(this.tableData());
    });

    effect(() => {
      this.columns$.next(this.columns());
    });

    effect(() => {
      this.totalRecordLength$.next(this.totalRecordLength());
    });

    effect(() => {
      this.selectAllData$.next(this.selectAllData());
    });
  }

  ngOnInit(): void {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((isEnabled: boolean) => {
      this.showAuditLogsFeature.set(isEnabled);
    });
    this.setupColumns();
    this.loadMilestones();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onSearch(value: string): void {
    this.searchText.set(value);
    this.applyClientFilters();
  }

  protected onStatusFilterChange(value: string): void {
    this.selectedStatusFilter = value;
    this.currentPage.set(1);
    this.loadMilestones();
  }

  protected showAuditTrail(): void {
    if (this.showAuditLogsFeature()) {
      this.showAuditTrailSidebar.set(true);
      this.cd.detectChanges();
    }
  }

  protected hideAuditTrail(): void {
    this.showAuditTrailSidebar.set(false);
    this.cd.detectChanges();
  }

  private applyClientFilters(): void {
    let filtered = [...this.allMilestones()];
    const search = this.searchText().trim().toLowerCase();
    if (search) {
      filtered = filtered.filter((m) =>
        m.name?.toLowerCase().includes(search)
      );
    }
    this.tableData.set(filtered);
  }

  private setupColumns(): void {
    this.translate
      .get([
        'MILESTONE.MILESTONE_NAME',
        'MILESTONE.MILESTONE_TYPE',
        'MILESTONE.TARGET',
        'MILESTONE.PERIOD',
        'MILESTONE.REWARD',
        'MILESTONE.STATUS',
      ])
      .subscribe((translations) => {
        this.columns.set([
          {
            title: translations['MILESTONE.MILESTONE_NAME'],
            classes: 'py-2 white-space-nowrap font-bold',
            sortable: true,
            filterable: true,
            dataProperty: 'name',
            type: 'component',
            data: {
              name: WithLinkComponent,
              fetch_key: 'name',
              additionData: true,
              aData: {
                keyName: 'name',
                uri: '/admin/milestone-configurations/edit',
              },
            },
          },
          {
            title: translations['MILESTONE.MILESTONE_TYPE'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'type',
            type: 'default',
          },
          {
            title: translations['MILESTONE.TARGET'],
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: true,
            dataProperty: 'target',
            type: 'default',
          },
          {
            title: translations['MILESTONE.PERIOD'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'period',
            type: 'default',
          },
          {
            title: translations['MILESTONE.REWARD'],
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: true,
            dataProperty: 'reward',
            type: 'default',
          },
          {
            title: translations['MILESTONE.STATUS'],
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: true,
            dataProperty: 'isActive',
            type: 'component',
            data: { name: EntityStatusComponent },
          },
          {
            title: '',
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['edit', 'status'],
            },
          },
        ]);
      });
  }

  loadMilestones(): void {
    this.loading.set(true);

    const params: { pageNumber: number; pageSize: number; isActive?: boolean } = {
      pageNumber: this.currentPage(),
      pageSize: this.pageSize(),
    };
    if (this.selectedStatusFilter === 'active') {
      params.isActive = true;
    } else if (this.selectedStatusFilter === 'inactive') {
      params.isActive = false;
    }

    this.milestoneService.listMilestoneConfigurations(params).subscribe({
      next: (response) => {
        const displayData = response.items.map(item => this.transformToDisplay(item));
        this.allMilestones.set(displayData);

        this.currentPage.set(response.pageNumber);
        this.pageSize.set(response.pageSize);
        this.totalRecordLength.set({
          totalPage: response.pageSize > 0
            ? Math.ceil(response.totalCount / response.pageSize)
            : 0,
          totalItems: response.totalCount,
          currentPage: response.pageNumber,
        });

        this.applyClientFilters();
        this.loading.set(false);
      },
      error: () => {
        this.toastService.showError('Failed to load milestone configurations');
        this.allMilestones.set([]);
        this.tableData.set([]);
        this.loading.set(false);
      }
    });
  }

  transformToDisplay(config: ListMilestoneConfigResponse): MilestoneConfigForDisplay {
    return {
      id: config.id,
      name: config.name || 'Unnamed Milestone',
      tierName: config.tierName,
      type: this.formatMilestoneType(config.milestoneType as MilestoneType),
      target: `${config.targetValue} ${this.formatUnit(config.unit || '')}`,
      period: this.formatPeriodType(config.periodType as MilestonePeriodType),
      reward: this.formatReward(config.rewardType as MilestoneRewardType),
      isActive: config.isActive,
      createdDate: config.createdDate
    };
  }

  formatMilestoneType(type?: MilestoneType | string): string {
    if (!type) return 'Unknown';
    const typeMap: Record<string, string> = {
      [MilestoneType.ClassCount]: 'Class Count',
      [MilestoneType.Distance]: 'Distance',
      [MilestoneType.AppointmentCount]: 'Appointment Count',
      [MilestoneType.TotalSessionCount]: 'Total Session Count'
    };
    return typeMap[type] || String(type);
  }

  formatUnit(unit: string): string {
    return unit.toLowerCase();
  }

  formatPeriodType(period?: MilestonePeriodType | string): string {
    if (!period) return 'Unknown';
    const periodMap: Record<string, string> = {
      [MilestonePeriodType.Lifetime]: 'Lifetime',
      [MilestonePeriodType.Weekly]: 'Weekly',
      [MilestonePeriodType.Monthly]: 'Monthly',
      [MilestonePeriodType.Custom]: 'Custom'
    };
    return periodMap[period] || String(period);
  }

  formatReward(rewardType?: MilestoneRewardType | string): string {
    if (!rewardType) return 'None';
    switch (rewardType) {
      case MilestoneRewardType.Badge:
        return 'Badge';
      case MilestoneRewardType.Points:
        return 'Points';
      case MilestoneRewardType.Discount:
        return 'Discount';
      default:
        return String(rewardType);
    }
  }

  protected onActionEvent(event: { action: string; columnData: MilestoneConfigForDisplay }): void {
    switch (event.action) {
      case 'edit':
        this.router.navigate([
          '/admin/milestone-configurations/edit',
          event.columnData.id,
        ]);
        break;
      case 'status':
        this.toggleStatus(event.columnData);
        break;
      case 'delete':
        this.deleteMilestone(event.columnData);
        break;
    }
  }

  private toggleStatus(milestone: MilestoneConfigForDisplay): void {
    const apiCall = milestone.isActive
      ? this.milestoneService.deactivateMilestoneConfiguration(milestone.id)
      : this.milestoneService.activateMilestoneConfiguration(milestone.id);

    apiCall.subscribe({
      next: () => {
        const messageKey = milestone.isActive
          ? 'Milestone configuration deactivated successfully'
          : 'Milestone configuration activated successfully';
        this.toastService.showSuccess(messageKey);
        this.loadMilestones();
      },
      error: () => {
        this.toastService.showError('Failed to update milestone configuration');
      },
    });
  }

  private deleteMilestone(_milestone: MilestoneConfigForDisplay): void {
    this.toastService.showError('Delete functionality not yet implemented');
  }

  onCreateMilestone(): void {
    this.router.navigate(['/admin/milestone-configurations/create']);
  }

  onPageChange(event: { ps: number; pn: number }): void {
    this.currentPage.set(event.pn);
    this.pageSize.set(event.ps);
    this.loadMilestones();
  }
}
